import { useParams, Link } from 'react-router-dom';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';

const articleDetailImages: Record<string, string> = {
  'article-1': 'https://readdy.ai/api/search-image?query=Modern%20creative%20studio%20workspace%20with%20warm%20lighting%2C%20minimalist%20design%2C%20content%20creation%20atmosphere%2C%20clean%20aesthetic&width=1200&height=600&seq=detail-article-1&orientation=landscape',
  'article-2': 'https://readdy.ai/api/search-image?query=Social%20media%20marketing%20dashboard%20with%20analytics%20charts%2C%20warm%20tone%2C%20modern%20office%20setup%2C%20professional%20environment&width=1200&height=600&seq=detail-article-2&orientation=landscape',
  'article-3': 'https://readdy.ai/api/search-image?query=Professional%20video%20production%20equipment%20setup%2C%20warm%20studio%20lighting%2C%20camera%20and%20lighting%20gear%2C%20creative%20atmosphere&width=1200&height=600&seq=detail-article-3&orientation=landscape',
  'article-4': 'https://readdy.ai/api/search-image?query=Brand%20strategy%20meeting%20with%20creative%20team%2C%20modern%20office%2C%20warm%20atmosphere%2C%20collaborative%20workspace&width=1200&height=600&seq=detail-article-4&orientation=landscape',
  'article-5': 'https://readdy.ai/api/search-image?query=Short%20video%20editing%20workstation%20with%20timeline%20display%2C%20warm%20ambient%20light%2C%20professional%20setup&width=1200&height=600&seq=detail-article-5&orientation=landscape',
  'article-6': 'https://readdy.ai/api/search-image?query=Digital%20marketing%20analytics%20on%20screen%2C%20warm%20modern%20office%2C%20data%20visualization%2C%20professional%20workplace&width=1200&height=600&seq=detail-article-6&orientation=landscape',
  'article-7': 'https://readdy.ai/api/search-image?query=Lifestyle%20social%20media%20content%20creation%2C%20smartphone%20photography%2C%20warm%20aesthetic%2C%20modern%20minimal%20workspace&width=1200&height=600&seq=detail-article-7&orientation=landscape',
  'article-8': 'https://readdy.ai/api/search-image?query=Brand%20visual%20design%20studio%2C%20graphic%20design%20workspace%2C%20warm%20creative%20atmosphere%2C%20modern%20aesthetic&width=1200&height=600&seq=detail-article-8&orientation=landscape',
  'article-9': 'https://readdy.ai/api/search-image?query=Youth%20culture%20digital%20content%2C%20video%20platform%20interface%2C%20warm%20modern%20environment%2C%20creative%20media&width=1200&height=600&seq=detail-article-9&orientation=landscape',
};

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;

  if (!article) {
    return (
      <main className="pt-16 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-3">文章未找到</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium text-sm">
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const relatedArticles = getRelatedArticles(article, 3);

  return (
    <main className="pt-16">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            author: { '@type': 'Person', name: article.author },
            datePublished: article.date,
            publisher: {
              '@type': 'Organization',
              name: '蜜桃传媒',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.mitaomedia.com/' },
              { '@type': 'ListItem', position: 2, name: article.category, item: `https://www.mitaomedia.com/category/${article.categorySlug}` },
              { '@type': 'ListItem', position: 3, name: article.title, item: `https://www.mitaomedia.com/article/${article.id}` },
            ],
          }),
        }}
      />

      <article className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
        <div className="max-w-4xl mx-auto">
          <nav className="flex items-center flex-wrap gap-2 text-sm text-foreground-400 mb-8" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to={`/category/${article.categorySlug}`} className="hover:text-primary-600 transition-colors">{article.category}</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-600 line-clamp-1">{article.title}</span>
          </nav>

          <div className="mb-8">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {article.tags.map((tag) => (
                <span key={tag} className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-400">
              <span className="flex items-center gap-1">
                <i className="ri-user-line text-xs"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line text-xs"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line text-xs"></i>
                {article.readTime}
              </span>
            </div>
          </div>

          <div className="mb-10 rounded-xl overflow-hidden">
            <img
              src={articleDetailImages[article.id] || articleDetailImages['article-1']}
              alt={article.title}
              title={article.title}
              className="w-full h-64 md:h-80 object-cover object-top"
            />
          </div>

          <div
            className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
              [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-heading [&_h2]:font-bold [&_h2]:text-foreground-900 [&_h2]:mt-8 [&_h2]:mb-4
              [&_p]:mb-4 [&_p]:leading-relaxed
              [&_strong]:text-foreground-900"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />
        </div>
      </article>

      {relatedArticles.length > 0 && (
        <section className="py-14 md:py-18 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h4 className="text-xl font-heading font-bold text-foreground-900 mb-8">
              相关推荐
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {relatedArticles.map((related) => (
                <Link
                  key={related.id}
                  to={`/article/${related.id}`}
                  className="group p-5 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                >
                  <span className="inline-block px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-3">
                    {related.category}
                  </span>
                  <h5 className="text-sm font-heading font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {related.title}
                  </h5>
                  <p className="text-foreground-400 text-xs">
                    {related.date} · {related.readTime}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}